import { useState, useEffect, useCallback } from 'react'

const RECENT_ORGANIZATION_SEARCHES_KEY = 'hubble_recent_organization_searches'
const MAX_STORED_SEARCHES = 20
const INITIAL_DISPLAY_COUNT = 5

interface SearchEntry {
  query: string
  timestamp: number
}

interface UseRecentOrganizationSearchesReturn {
  recentSearches: SearchEntry[]
  addRecentSearch: (search: string) => void
}

const useRecentOrganizationSearches =
  (): UseRecentOrganizationSearchesReturn => {
    const [recentSearches, setRecentSearches] = useState<SearchEntry[]>([])

    const loadFromStorage = useCallback(() => {
      try {
        const stored = localStorage.getItem(RECENT_ORGANIZATION_SEARCHES_KEY)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed)) {
            const migrated = parsed.map((item) => {
              if (typeof item === 'string') {
                return { query: item, timestamp: Date.now() }
              }
              return item
            })
            setRecentSearches((prev) => {
              if (JSON.stringify(prev) !== JSON.stringify(migrated)) {
                return migrated
              }
              return prev
            })
          }
        }
      } catch (error) {
        console.error('Error loading recent organization searches:', error)
      }
    }, [])

    // Load from localStorage on mount and poll for changes
    useEffect(() => {
      loadFromStorage()

      const interval = setInterval(() => {
        loadFromStorage()
      }, 1000)

      return () => clearInterval(interval)
    }, [loadFromStorage])

    useEffect(() => {
      if (recentSearches.length > 0) {
        try {
          localStorage.setItem(
            RECENT_ORGANIZATION_SEARCHES_KEY,
            JSON.stringify(recentSearches)
          )
        } catch (error) {
          console.error('Error saving recent organization searches:', error)
        }
      }
    }, [recentSearches])

    const addRecentSearch = useCallback((search: string) => {
      const trimmedSearch = search.trim()
      if (!trimmedSearch) return

      setRecentSearches((prev) => {
        const filtered = prev.filter((s) => s.query !== trimmedSearch)
        const newEntry: SearchEntry = {
          query: trimmedSearch,
          timestamp: Date.now(),
        }
        const updated = [newEntry, ...filtered]
        return updated.slice(0, MAX_STORED_SEARCHES)
      })
    }, [])

    return {
      recentSearches,
      addRecentSearch,
    }
  }

export default useRecentOrganizationSearches
export { INITIAL_DISPLAY_COUNT }
